import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import axios from 'axios';
import { toast } from "react-toastify";

const DashboardPublicDetailsModal = ({ show, handleClose, province }) => {
  const [districtData, setDistrictData] = useState([]);
  const [loading, setLoading] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  // Wrap fetchDistrictData in useCallback to stabilize it
  const fetchDistrictData = useCallback(async (provinceName) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const districtResponse = await axios.post(
        `${API_BASE_URL}/api/plantation/pafs/only-public-district-summary`,
        { province: provinceName },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (districtResponse.data.status) {
        setDistrictData(districtResponse.data.data);
      } else {
        showToast("Failed to fetch district data.", "error");
      }
      
    } catch (error) {
      console.error("Error fetching district data:", error);
      showToast("Failed to fetch district data. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]); // Only recreate if API_BASE_URL changes

  // Now include fetchDistrictData in deps
  useEffect(() => {
    if (show && province) {
      fetchDistrictData(province);
    }
  }, [show, province, fetchDistrictData]);

  const showToast = (message, type = "success") => {
    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    };

    switch (type) {
      case "success":
        toast.success(message, toastOptions);
        break;
      case "error":
        toast.error(message, toastOptions);
        break;
      default:
        toast(message, toastOptions);
    }
  };
  
  return (
    <Modal show={show} onHide={handleClose} size="xl" centered scrollable>
      <Modal.Header closeButton className="bg-light">
        <img
          src={process.env.PUBLIC_URL + "/assets/images/govpklogo.png"}
          alt="Government of Pakistan Logo"
          className="mx-20 my-4"
          style={{ width: "90px", height: "auto" }}
        />
        <Modal.Title className="h5">
          District Summary {province && `- ${province}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover responsive>
              <thead className="table-light">
                <tr>
                  <th>District</th>
                  <th>Total Users</th>
                  <th>Total Activities</th>
                  <th>Total Plants</th>
                </tr>
              </thead>
              <tbody>
                {districtData.length > 0 ? (
                  districtData.map((district, index) => (
                    <tr key={index}>
                      <td>
                        <h6 className="text-md mb-0 fw-normal">
                          {district.district || "Unknown District"}
                        </h6>
                      </td>
                      <td>{district.totalUsers}</td>
                      <td>{district.totalActivities}</td>
                      <td>{district.totalPlants.toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      <div className="d-flex flex-column align-items-center">
                        <i className="mdi mdi-database-off text-muted fs-1 mb-2"></i>
                        <p className="text-muted">
                          {province ? `No district data available for ${province}` : 'No district data available'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light">
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Close
        </Button>
        <Button 
          variant="primary" 
          size="sm" 
          onClick={() => fetchDistrictData(province)} 
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DashboardPublicDetailsModal;